import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../../utils/axios";
import {
  Search,
  Clock,
  Activity,
  Globe,
  Loader2,
  Filter,
} from "lucide-react";

interface AuditLogItem {
  id: number | string;
  actor_id: number;
  actor_role: string;
  actor_email: string;
  action: string;
  target_type: string;
  target_id: string;
  metadata: any;
  ip_address: string;
  created_at: string;
}

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get("/audit-logs", {
        params: {
          page,
          limit: 20,
          search,
          action: actionFilter || undefined,
        },
      });

      if (res.data?.success) {
        setLogs(res.data.data.logs || []);
        setTotalPages(res.data.data.pagination.totalPages || 1);
        setTotalCount(res.data.data.pagination.total || 0);
      }
    } catch (err: any) {
      console.error(err);
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const getActionBadge = (action: string) => {
    if (action.includes("CREATE")) {
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    }
    if (action.includes("DISABLE") || action.includes("BLOCK") || action.includes("DELETE")) {
      return "bg-red-50 text-red-700 border-red-200";
    }
    if (action.includes("EDIT") || action.includes("UPDATE") || action.includes("RESET")) {
      return "bg-amber-50 text-amber-700 border-amber-200";
    }
    if (action.includes("LOGIN")) {
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    }
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
            <Activity size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Security & Audit Logs</h1>
            <p className="text-sm text-gray-500">
              Immutable audit trail recording administrative and sensitive system operations.
            </p>
          </div>
        </div>

        <span className="text-xs font-semibold px-3 py-1.5 rounded-xl bg-gray-100 text-gray-700">
          Total Recorded: {totalCount}
        </span>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <form onSubmit={handleSearch} className="flex-1 max-w-md w-full relative">
          <Search size={18} className="absolute left-3.5 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search by actor, action or target..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </form>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Filter size={16} className="text-gray-400" />
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
            className="px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Actions</option>
            <option value="ADMIN_CREATE">Admin Create</option>
            <option value="ADMIN_EDIT">Admin Edit</option>
            <option value="ADMIN_DISABLE">Admin Disable</option>
            <option value="BACKOFFICE_CREATE">Back Office Create</option>
            <option value="AGENT_CREATE">Agent Create</option>
            <option value="AGENT_DISABLE">Agent Disable</option>
            <option value="PERMISSION_ASSIGN">Permission Assign</option>
            <option value="LOGIN">Logins</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4">Timestamp</th>
                <th className="px-6 py-4">Actor</th>
                <th className="px-6 py-4">Role</th>
                <th className="px-6 py-4">Action</th>
                <th className="px-6 py-4">Target</th>
                <th className="px-6 py-4">Metadata</th>
                <th className="px-6 py-4">IP Address</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                    <Loader2 className="animate-spin inline-block mr-2" size={20} />
                    Loading audit trail...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                    No audit records found.
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  return (
                    <tr key={log.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 text-xs font-mono text-gray-500 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Clock size={12} className="text-gray-400" />
                          {new Date(log.created_at).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-800 text-xs">
                          {log.actor_email || `ID #${log.actor_id}`}
                        </div>
                        <span className="text-[11px] text-gray-400">UID: #{log.actor_id}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-0.5 rounded text-[11px] font-mono font-semibold bg-gray-100 text-gray-700">
                          {log.actor_role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getActionBadge(
                            log.action
                          )}`}
                        >
                          {log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs">
                        {log.target_type ? (
                          <div className="font-mono text-gray-700">
                            {log.target_type} #{log.target_id || "N/A"}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs max-w-xs truncate">
                        {log.metadata ? (
                          <code className="text-[11px] bg-gray-50 text-gray-600 p-1 rounded font-mono block truncate">
                            {typeof log.metadata === "object"
                              ? JSON.stringify(log.metadata)
                              : log.metadata}
                          </code>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs font-mono text-gray-500">
                        <div className="flex items-center gap-1">
                          <Globe size={12} className="text-gray-400" />
                          {log.ip_address || "127.0.0.1"}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
